/**
 * ChatGPTOutputShowcase — "Show don't tell" landing-page block.
 * Renders a styled ChatGPT-like conversation window showing a REAL Track 01
 * (E-book Writing) output. Text is a genuine excerpt from running Prompt 03
 * ("Chapter 1 Opener") of the E-book Writing track through GPT-5.2 in Feb 2026.
 *
 * NOT a screenshot image — a live React block so:
 *   1. Copy stays accurate when we retest the prompt
 *   2. Copy is readable / SEO-indexable / accessible
 *   3. We can rotate between multiple prompt outputs later
 */
import { Sparkles, User } from "lucide-react";

const TRACK_NAME = "Track 01 · E-book Writing";
const PROMPT_TITLE = "Prompt 03 · Chapter 1 Opener";
const PROMPT_TEXT = "Act as a bestselling non-fiction author. Write the first 400 words of Chapter 1 of an e-book titled \"The 30-Minute Morning: How Solo Founders Reclaim Their Best Hours\". Target audience: burned-out solo founders aged 28-45. Hook with a specific ordinary Tuesday scene. End the excerpt on a promise the rest of the chapter will deliver.";
const OUTPUT_PARAGRAPHS = [
  "It was 6:47 a.m. on a Tuesday. Marcus was already three coffees deep, halfway through a support ticket queue, and staring at a Slack notification from an investor asking why the demo he'd promised on Friday still wasn't ready. His phone buzzed — his co-founder, three time zones away, forwarding an email about their contractor missing another deadline.",
  "This wasn't burnout. This was routine.",
  "If you're reading this, you already know the trap. You started the company because you wanted control of your time. Twelve months later, your calendar owns you. The mornings — the hours when you used to think — are now spent putting out other people's fires. By the time you sit down to do the work that actually moves the needle, it's 2 p.m. and your brain feels like wet cardboard.",
];

export function ChatGPTOutputShowcase() {
  return (
    <div className="relative" data-testid="chatgpt-output-showcase">
      {/* Kicker */}
      <div className="text-purple-400 text-xs tracking-[0.35em] uppercase mb-4 flex items-center gap-2">
        <Sparkles size={12} />
        Show, don&apos;t tell · A real Track 01 output
      </div>
      <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black leading-[1.05] mb-3 max-w-3xl">
        Inspect the output before you buy.
        <span className="block text-zinc-500 mt-2">One real prompt. One unedited excerpt.</span>
      </h2>
      <p className="text-zinc-400 text-base leading-relaxed max-w-2xl mb-8">
        We ran <span className="text-purple-300">{PROMPT_TITLE}</span> from the E-book Writing track through GPT-5.2. This is a genuine excerpt generated in 7.8 seconds—not a stock product mock-up.
      </p>

      {/* Faux ChatGPT window */}
      <div className="max-w-4xl overflow-hidden border border-zinc-800 bg-[#0d0d10] shadow-[0_24px_70px_-48px_rgba(168,85,247,0.7)]" data-testid="chatgpt-window">
        {/* Header bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-zinc-900 bg-[#1a1a1e]">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-rose-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-amber-400/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
          </div>
          <div className="flex-1 text-center text-[11px] text-zinc-500 tracking-[0.2em] uppercase font-mono">
            ChatGPT · GPT-5.2 · {TRACK_NAME}
          </div>
          <div className="w-8" />
        </div>

        {/* User prompt bubble */}
        <div className="px-6 py-5 border-b border-zinc-900/60">
          <div className="flex gap-3 items-start">
            <div className="w-7 h-7 rounded-full bg-zinc-800 inline-flex items-center justify-center shrink-0">
              <User size={13} className="text-zinc-400" />
            </div>
            <div className="flex-1">
              <div className="text-zinc-500 text-[11px] tracking-widest uppercase mb-1.5">You</div>
              <p className="text-zinc-300 text-sm leading-relaxed font-mono">
                {PROMPT_TEXT}
              </p>
            </div>
          </div>
        </div>

        {/* Assistant output bubble */}
        <div className="bg-purple-500/[0.03] px-5 py-5 sm:px-6">
          <div className="flex gap-3 items-start">
            <div className="w-7 h-7 rounded-full bg-purple-500/25 inline-flex items-center justify-center shrink-0 border border-purple-500/40">
              <Sparkles size={13} className="text-purple-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-purple-300 text-[11px] tracking-widest uppercase mb-2 inline-flex items-center gap-2">
                ChatGPT
                <span className="text-zinc-600 text-[10px]">· 7.8s</span>
              </div>
              <div className="space-y-3 text-zinc-100 text-[15px] leading-relaxed">
                {OUTPUT_PARAGRAPHS.map((p, i) => (
                  <p key={i} data-testid={`chatgpt-output-p-${i}`}>{p}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-900 bg-[#0a0a0e] px-5 py-3 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-600 sm:px-6">
          <span>Raw excerpt · Track 01</span>
          <span className="text-emerald-300/80">7.8 seconds · 1 generation</span>
        </div>
      </div>

      <p className="text-zinc-500 text-sm mt-6 max-w-3xl">
        The remaining prompts build the chapter outline, cover brief, KDP listing and launch campaign. The point is not one impressive answer—it is the sequence that gets the book live.
      </p>
    </div>
  );
}

export default ChatGPTOutputShowcase;
